import { useState, useEffect } from 'react'
import { X, Upload, Link } from 'lucide-react'
import { supabase } from '../services/supabase'
import Button from './Button'
import MaterialUploadAlternativo from './MaterialUploadAlternativo'
import MaterialList from './MaterialList'
import type { MaterialAula } from '../services/supabase'

interface Aula {
    id: string
    titulo: string
    descricao: string | null
    video_url: string | null
    duracao: number | null
    ordem_exibicao: number
    status: string
}

interface ModalAulaProps {
    isOpen: boolean
    onClose: () => void
    onSuccess: () => void
    onUpdateList?: (aulaId?: string) => void
    aula?: Aula | null
    moduloId: string
    showSuccess: (title: string, message?: string) => void
    showError: (title: string, message?: string) => void
}

export default function ModalAula({ isOpen, onClose, onSuccess, onUpdateList, aula, moduloId, showSuccess, showError }: ModalAulaProps) {
    // showError é usado implicitamente pelo componente pai para exibir erros
    void showError;
    const [formData, setFormData] = useState({
        titulo: '',
        descricao: '',
        video_url: '',
        duracao: '',
        ordem_exibicao: '1',
        status: 'ativo',
        aulaId: ''
    })
    const [videoFile, setVideoFile] = useState<File | null>(null)
    const [videoPreview, setVideoPreview] = useState<string>('')
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState('')
    const [createdAulaId, setCreatedAulaId] = useState<string | null>(null)
    const [showMaterialList, setShowMaterialList] = useState(false)
    const [uploadProgress, setUploadProgress] = useState<number>(0)
    const [isUploading, setIsUploading] = useState(false)

    useEffect(() => {
        console.log('🔄 useEffect executado! aula?.id:', aula?.id, 'isOpen:', isOpen)

        if (isOpen) {
            if (aula) {
                console.log('📝 ModalAula: Carregando dados da aula EXISTENTE:', aula.id)
                console.log('📝 Dados completos da aula:', aula)

                const newFormData = {
                    titulo: aula.titulo,
                    descricao: aula.descricao || '',
                    video_url: aula.video_url || '',
                    duracao: aula.duracao?.toString() || '',
                    ordem_exibicao: aula.ordem_exibicao.toString(),
                    status: aula.status,
                    aulaId: aula.id
                }

                console.log('📝 formData que será definido:', newFormData)

                setFormData(newFormData)
                setVideoPreview(aula.video_url || '')
                setCreatedAulaId(aula.id)
            } else {
                console.log('🆕 ModalAula: Resetando formulário para nova aula')
                resetForm()
            }
        }
    }, [aula?.id, isOpen]) // Dependências: apenas quando o ID da aula ou o estado do modal muda

    const resetForm = () => {
        setFormData({
            titulo: '',
            descricao: '',
            video_url: '',
            duracao: '',
            ordem_exibicao: '1',
            status: 'ativo',
            aulaId: ''
        })
        setVideoFile(null)
        setVideoPreview('')
        setError('')
        setCreatedAulaId(null)
    }

    const handleVideoFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0]
        if (file) {
            setVideoFile(file)
            const reader = new FileReader()
            reader.onload = (e) => {
                setVideoPreview(e.target?.result as string)
            }
            reader.readAsDataURL(file)
        }
    }

    // Função para extrair o caminho do arquivo da URL do Supabase Storage
    const extractFilePathFromUrl = (url: string): string | null => {
        try {
            // URLs do Supabase Storage têm formatos como:
            // https://[project].supabase.co/storage/v1/object/public/videos/[caminho]
            // https://[project].supabase.co/storage/v1/object/sign/videos/[caminho]
            // https://[project].supabase.co/storage/v1/object/videos/[caminho]
            
            const urlObj = new URL(url)
            const pathParts = urlObj.pathname.split('/').filter(part => part !== '')
            
            // Procurar pelo índice de 'videos' e pegar tudo depois
            const videosIndex = pathParts.indexOf('videos')
            if (videosIndex !== -1 && videosIndex < pathParts.length - 1) {
                // Pegar tudo depois de 'videos'
                const filePath = pathParts.slice(videosIndex + 1).join('/')
                // O método remove() do Supabase precisa apenas do caminho relativo ao bucket
                return filePath
            }
            
            // Tentar extrair diretamente se a URL contém 'videos/'
            const videosMatch = url.match(/videos\/(.+)$/)
            if (videosMatch && videosMatch[1]) {
                return videosMatch[1]
            }
            
            return null
        } catch (err) {
            console.error('Erro ao extrair caminho do arquivo:', err)
            return null
        }
    }

    // Função para remover vídeo antigo do storage
    const removeOldVideo = async (videoUrl: string): Promise<void> => {
        try {
            const filePath = extractFilePathFromUrl(videoUrl)
            if (!filePath) {
                console.log('⚠️ Não foi possível extrair o caminho do vídeo antigo da URL:', videoUrl)
                return
            }

            console.log('🗑️ Removendo vídeo antigo do storage:', filePath)
            const { error } = await supabase.storage
                .from('videos')
                .remove([filePath])

            if (error) {
                console.error('❌ Erro ao remover vídeo antigo:', error)
                // Não lançar erro, apenas logar, pois o upload do novo vídeo pode continuar
            } else {
                console.log('✅ Vídeo antigo removido com sucesso:', filePath)
            }
        } catch (err) {
            console.error('❌ Erro ao remover vídeo antigo:', err)
            // Não lançar erro, apenas logar, pois não queremos bloquear o upload do novo vídeo
        }
    }

    const uploadVideo = async (file: File): Promise<string | null> => {
        try {
            setIsUploading(true)
            setUploadProgress(0)

            const fileExt = file.name.split('.').pop()
            const fileName = `${Math.random()}.${fileExt}`
            const filePath = `videos/${fileName}`

            // Obter URL e token para upload
            const { data: { session } } = await supabase.auth.getSession()
            if (!session) {
                throw new Error('Usuário não autenticado')
            }

            // Obter a URL base do Supabase da variável de ambiente
            const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
            if (!supabaseUrl) {
                throw new Error('URL do Supabase não configurada')
            }

            // Fazer upload usando XMLHttpRequest para rastrear progresso
            const uploadUrl = `${supabaseUrl}/storage/v1/object/videos/${filePath}`
            
            return new Promise((resolve, reject) => {
                const xhr = new XMLHttpRequest()

                xhr.upload.addEventListener('progress', (e) => {
                    if (e.lengthComputable) {
                        const percentComplete = (e.loaded / e.total) * 100
                        setUploadProgress(Math.round(percentComplete))
                    }
                })

                xhr.addEventListener('load', () => {
                    if (xhr.status === 200 || xhr.status === 201) {
                        const { data } = supabase.storage
                            .from('videos')
                            .getPublicUrl(filePath)
                        setUploadProgress(100)
                        setTimeout(() => {
                            setIsUploading(false)
                            setUploadProgress(0)
                        }, 500)
                        resolve(data.publicUrl)
                    } else {
                        setIsUploading(false)
                        setUploadProgress(0)
                        reject(new Error(`Erro no upload: ${xhr.statusText}`))
                    }
                })

                xhr.addEventListener('error', () => {
                    setIsUploading(false)
                    setUploadProgress(0)
                    reject(new Error('Erro ao fazer upload do vídeo'))
                })

                xhr.addEventListener('abort', () => {
                    setIsUploading(false)
                    setUploadProgress(0)
                    reject(new Error('Upload cancelado'))
                })

                xhr.open('POST', uploadUrl)
                xhr.setRequestHeader('Authorization', `Bearer ${session.access_token}`)
                xhr.setRequestHeader('Content-Type', file.type || 'video/mp4')
                xhr.setRequestHeader('x-upsert', 'true') // Permite sobrescrever se existir
                xhr.send(file)
            })
        } catch (err) {
            console.error('Erro ao fazer upload do vídeo:', err)
            setIsUploading(false)
            setUploadProgress(0)
            return null
        }
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        console.log('🚀 ========== SUBMIT DO FORMULÁRIO CHAMADO ==========')
        console.log('📋 formData atual:', formData)
        console.log('🔍 Variável aula existe?:', !!aula)
        console.log('🆔 ID da aula:', aula?.id)
        console.log('🆔 createdAulaId:', createdAulaId)
        console.log('🔍 Vai ATUALIZAR ou CRIAR?', aula ? 'ATUALIZAR' : 'CRIAR')
        setError('')
        setLoading(true)

        try {
            // Validações
            if (!formData.titulo.trim()) {
                console.log('❌ Validação falhou: Título é obrigatório')
                setError('Título é obrigatório')
                setLoading(false)
                return
            }

            if (!formData.ordem_exibicao || parseInt(formData.ordem_exibicao) <= 0) {
                console.log('❌ Validação falhou: Ordem de exibição')
                setError('Ordem de exibição deve ser maior que zero')
                setLoading(false)
                return
            }

            // Upload do vídeo se houver
            let videoUrl = formData.video_url
            if (videoFile) {
                // Se estiver editando uma aula existente e ela já tiver um vídeo do Supabase Storage, remover o vídeo antigo
                if (aula && aula.video_url) {
                    // Verificar se o vídeo antigo é do Supabase Storage (não é URL externa como YouTube/Vimeo)
                    const isSupabaseStorageUrl = aula.video_url.includes('supabase.co') || aula.video_url.includes('supabase')
                    if (isSupabaseStorageUrl) {
                        console.log('🔄 Removendo vídeo antigo do Supabase Storage antes de fazer upload do novo')
                        await removeOldVideo(aula.video_url)
                    } else {
                        console.log('ℹ️ Vídeo antigo é URL externa, não será removido do storage')
                    }
                }
                
                const uploadedUrl = await uploadVideo(videoFile)
                if (uploadedUrl) {
                    videoUrl = uploadedUrl
                }
            }

            const aulaData = {
                titulo: formData.titulo.trim(),
                descricao: formData.descricao.trim() || null,
                video_url: videoUrl || null,
                duracao: formData.duracao ? parseInt(formData.duracao) : null,
                ordem_exibicao: parseInt(formData.ordem_exibicao),
                status: formData.status,
                modulo_id: moduloId
            }

            console.log('📝 Dados a serem salvos:', aulaData)
            console.log('🎥 videoUrl final:', videoUrl)
            console.log('📝 formData.video_url original:', formData.video_url)

            if (aula) {
                // Atualizar aula existente
                console.log('🔄 Atualizando aula EXISTENTE:', aula.id)
                console.log('📊 Dados que serão atualizados:', JSON.stringify(aulaData, null, 2))

                const { data, error } = await supabase
                    .from('aulas')
                    .update(aulaData)
                    .eq('id', aula.id)
                    .select()

                if (error) {
                    console.error('❌ ERRO ao atualizar:', error)
                    console.error('❌ Detalhes do erro:', JSON.stringify(error, null, 2))
                    throw error
                }

                console.log('✅ Aula atualizada com SUCESSO:', data)
                console.log('📊 Resposta do banco:', JSON.stringify(data, null, 2))
                showSuccess('Aula atualizada com sucesso!')

                // Recarregar a lista de aulas no componente pai sem fechar o modal
                // Passando o ID da aula para que o componente pai possa atualizar o editingAula
                if (onUpdateList) {
                    console.log('🔄 Chamando onUpdateList com aulaId:', aula.id)
                    onUpdateList(aula.id)
                }

                // Manter modal aberto para gerenciar materiais
                setCreatedAulaId(aula.id)
            } else {
                // Criar nova aula
                console.log('➕ Criando NOVA aula')
                console.log('📊 Dados que serão inseridos:', JSON.stringify(aulaData, null, 2))

                const { data: newAula, error } = await supabase
                    .from('aulas')
                    .insert(aulaData)
                    .select('id')
                    .single()

                if (error) {
                    console.error('❌ ERRO ao criar:', error)
                    throw error
                }

                console.log('✅ Aula criada com SUCESSO:', newAula.id)
                showSuccess('Aula criada com sucesso!')

                // Mostrar seção de materiais após criar
                setCreatedAulaId(newAula.id)
            }
        } catch (err) {
            console.error('❌ Erro capturado ao salvar aula:', err)
            if (err instanceof Error) {
                console.error('❌ Mensagem de erro:', err.message)
                setError(`Erro ao salvar aula: ${err.message}`)
            } else {
                setError('Erro ao salvar aula. Tente novamente.')
            }
            // Resetar estado de upload em caso de erro
            setIsUploading(false)
            setUploadProgress(0)
        } finally {
            setLoading(false)
        }
    }


    const handleClose = () => {
        console.log('🚪 handleClose chamado!')
        resetForm()
        onClose()
    }

    // Funções para lidar com upload de materiais
    const handleMaterialUploadSuccess = (_material: MaterialAula) => {
        showSuccess('Material adicionado com sucesso!')
        loadMateriais()
    }

    const handleMaterialUploadError = (error: string) => {
        showError(`Erro ao adicionar material: ${error}`)
    }

    const handleFinishMaterials = () => {
        console.log('🎯 handleFinishMaterials chamado!')
        console.log('🎯 Fechando modal e atualizando lista...')
        onSuccess()
        onClose()
        resetForm()
        console.log('🎯 Modal fechado e lista atualizada!')
    }

    // Carregar materiais existentes (usado para atualizar lista após upload)
    const loadMateriais = async () => {
        if (!createdAulaId) return

        try {
            // LOG: Verificar usuário
            const { data: { user } } = await supabase.auth.getUser()
            console.log('🔐 ModalAula - Usuário:', user?.email || 'Não logado')

            // LOG: Tentar ler
            console.log('🔍 ModalAula - Tentando ler materiais para aula:', createdAulaId)

            const { data, error } = await supabase
                .from('material_aula')
                .select('*')
                .eq('aula_id', createdAulaId)
                .eq('status', 'ativo')
                .order('ordem_exibicao', { ascending: true })

            if (error) {
                console.error('❌ ModalAula - Erro:', error)
                throw error
            }

            console.log('✅ ModalAula - Materiais carregados:', data?.length || 0)
            // Materiais carregados são usados pelo componente MaterialList via reload
        } catch (err) {
            console.error('❌ ModalAula - Erro ao carregar materiais:', err)
        }
    }

    // Alternar entre visualização de materiais e upload
    const toggleMaterialView = () => {
        console.log('🔄 Alternando visualização de materiais. showMaterialList:', showMaterialList, 'createdAulaId:', createdAulaId)
        setShowMaterialList(!showMaterialList)
        if (!showMaterialList && createdAulaId) {
            loadMateriais()
        }
    }

    if (!isOpen) {
        console.log('ModalAula: Modal fechado, não renderizando')
        return null
    }

    console.log('ModalAula: Renderizando modal com dados:', { 
        aula, 
        moduloId, 
        isOpen,
        'aula?.id': aula?.id,
        'aula?.titulo': aula?.titulo,
        'formData.titulo': formData.titulo
    })
    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                        {aula ? 'Editar Aula' : 'Nova Aula'}
                    </h2>
                    <button
                        onClick={handleClose}
                        className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                    >
                        <X size={24} />
                    </button>
                </div>

                {/* Form */}
                <form onSubmit={(e) => {
                    console.log('📝 Form submit chamado!', e)
                    handleSubmit(e)
                }} className="p-6 space-y-6">
                    {/* Erro */}
                    {error && (
                        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                            <p className="text-red-800 dark:text-red-200 text-sm">{error}</p>
                        </div>
                    )}

                    {/* Título */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Título da Aula *
                        </label>
                        <input
                            type="text"
                            value={formData.titulo}
                            onChange={(e) => setFormData({ ...formData, titulo: e.target.value })}
                            className="w-full p-3 border border-gray-200 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400"
                            placeholder="Ex: Introdução às Técnicas de Vendas"
                            required
                        />
                    </div>

                    {/* Descrição */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Descrição
                        </label>
                        <textarea
                            value={formData.descricao}
                            onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
                            rows={4}
                            className="w-full p-3 border border-gray-200 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400"
                            placeholder="Descreva o conteúdo desta aula..."
                        />
                    </div>

                    {/* Grid de campos */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Duração */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Duração (minutos)
                            </label>
                            <input
                                type="number"
                                value={formData.duracao}
                                onChange={(e) => setFormData({ ...formData, duracao: e.target.value })}
                                className="w-full p-3 border border-gray-200 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400"
                                placeholder="30"
                                min="1"
                            />
                        </div>

                        {/* Ordem de Exibição */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Ordem de Exibição *
                            </label>
                            <input
                                type="number"
                                value={formData.ordem_exibicao}
                                onChange={(e) => setFormData({ ...formData, ordem_exibicao: e.target.value })}
                                className="w-full p-3 border border-gray-200 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400"
                                placeholder="1"
                                min="1"
                                required
                            />
                        </div>
                    </div>

                    {/* Status */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Status *
                        </label>
                        <select
                            value={formData.status}
                            onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                            className="w-full p-3 border border-gray-200 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                            required
                        >
                            <option value="ativo">Ativo</option>
                            <option value="inativo">Inativo</option>
                        </select>
                    </div>

                    {/* URL do Vídeo */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            URL do Vídeo
                        </label>
                        <div className="flex items-center gap-2">
                            <Link className="h-5 w-5 text-gray-400" />
                            <input
                                type="url"
                                value={formData.video_url}
                                onChange={(e) => {
                                    console.log('🎥 Mudando URL do vídeo:', e.target.value)
                                    setFormData({ ...formData, video_url: e.target.value })
                                }}
                                className="flex-1 p-3 border border-gray-200 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400"
                                placeholder="https://youtube.com/watch?v=... ou https://vimeo.com/..."
                            />
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            Cole a URL do vídeo (YouTube, Vimeo, etc.) ou faça upload de um arquivo
                        </p>
                    </div>

                    {/* Upload de Vídeo */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Upload de Vídeo
                        </label>
                        <div className="space-y-4">
                            {/* Preview do vídeo */}
                            {videoPreview && (
                                <div className="relative w-full h-48 bg-gray-100 dark:bg-gray-700 rounded-lg overflow-hidden">
                                    {videoPreview.includes('blob:') ? (
                                        <video
                                            src={videoPreview}
                                            controls
                                            className="w-full h-full object-cover"
                                        />
                                    ) : (
                                        <iframe
                                            src={videoPreview}
                                            className="w-full h-full"
                                            allowFullScreen
                                        />
                                    )}
                                </div>
                            )}

                            {/* Input de upload */}
                            <div className="flex items-center justify-center w-full">
                                <label className={`flex flex-col items-center justify-center w-full h-32 border-2 border-gray-300 dark:border-gray-600 border-dashed rounded-lg cursor-pointer bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors ${isUploading ? 'opacity-50 cursor-not-allowed' : ''}`}>
                                    <div className="flex flex-col items-center justify-center pt-5 pb-6">
                                        <Upload className="w-8 h-8 mb-4 text-gray-500 dark:text-gray-400" />
                                        <p className="mb-2 text-sm text-gray-500 dark:text-gray-400">
                                            <span className="font-semibold">Clique para upload</span> ou arraste e solte
                                        </p>
                                        <p className="text-xs text-gray-500 dark:text-gray-400">
                                            MP4, AVI, MOV até 100MB
                                        </p>
                                    </div>
                                    <input
                                        type="file"
                                        className="hidden"
                                        accept="video/*"
                                        onChange={handleVideoFileChange}
                                        disabled={isUploading}
                                    />
                                </label>
                            </div>

                            {/* Barra de Progresso */}
                            {isUploading && (
                                <div className="w-full">
                                    <div className="flex items-center justify-between mb-2">
                                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                            Enviando vídeo...
                                        </span>
                                        <span className="text-sm text-gray-600 dark:text-gray-400">
                                            {uploadProgress}%
                                        </span>
                                    </div>
                                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5">
                                        <div
                                            className="bg-indigo-600 h-2.5 rounded-full transition-all duration-300 ease-out"
                                            style={{ width: `${uploadProgress}%` }}
                                        ></div>
                                    </div>
                                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                        Aguarde enquanto o vídeo é enviado para o servidor...
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Seção de Materiais - Mostrar sempre que houver aulaId (criada ou editando) */}
                    {createdAulaId && (
                        <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
                            <div className="mb-4">
                                <div className="flex items-center justify-between mb-2">
                                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                                        Materiais da Aula
                                    </h3>
                                    <button
                                        type="button"
                                        onClick={toggleMaterialView}
                                        className="text-sm text-indigo-600 hover:text-indigo-700 dark:text-indigo-400 dark:hover:text-indigo-300"
                                    >
                                        {showMaterialList ? 'Fazer Upload' : 'Ver Materiais Existentes'}
                                    </button>
                                </div>
                                <p className="text-sm text-gray-600 dark:text-gray-400">
                                    Adicione materiais complementares como PDFs, documentos, planilhas, etc.
                                </p>
                            </div>

                            {showMaterialList ? (
                                <MaterialList
                                    aulaId={createdAulaId}
                                    className="mb-4"
                                    showUploadButton={false}
                                />
                            ) : (
                                <MaterialUploadAlternativo
                                    aulaId={createdAulaId}
                                    onUploadSuccess={handleMaterialUploadSuccess}
                                    onUploadError={handleMaterialUploadError}
                                    className="mb-4"
                                />
                            )}
                        </div>
                    )}


                    {/* Botões - SEMPRE mostrados no final */}
                    <div className="flex justify-end gap-3 pt-6 border-t border-gray-200 dark:border-gray-700">
                        <Button
                            type="button"
                            variant="btn_secondary"
                            onClick={handleClose}
                            disabled={loading}
                        >
                            Cancelar
                        </Button>
                        <Button
                            type="submit"
                            variant="btn_add"
                            loading={loading}
                            disabled={loading}
                            onClick={() => {
                                console.log('🔘 Botão de salvar clicado! Tipo:', aula ? 'EDITAR' : 'CRIAR')
                                console.log('🔘 aula:', aula)
                                console.log('🔘 formData:', formData)
                            }}
                        >
                            {aula ? 'Salvar Alterações' : 'Criar Aula'}
                        </Button>
                        {createdAulaId && !aula && (
                            <Button
                                type="button"
                                variant="btn_secondary"
                                onClick={handleFinishMaterials}
                                disabled={loading}
                            >
                                Fechar e Finalizar
                            </Button>
                        )}
                    </div>
                </form>
            </div>
        </div>
    )
}
